/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeftIcon,
  CircleStackIcon,
  CubeIcon,
  GlobeAltIcon,
  PencilIcon,
  PhotoIcon,
  Square3Stack3DIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  DockerHost,
  getDockerImages,
  getDockerInfo,
  getDockerLiveContainers,
  getDockerNetworks,
  getDockerVolumes,
  DockerLiveContainer,
} from "@/api/docker";
import { Bar, StatusDot } from "@/components/servers/kit";
import { ContainersView, ContainerTab } from "./ContainersView";
import { ContainerPage } from "./ContainerPage";
import { HostView as View, StateBadge, fmtDate, fmtSize, shortID } from "./shared";

interface Props {
  host: DockerHost;
  canGoBack: boolean;
  onBack: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

const NAV: { id: View; label: string }[] = [
  { id: "dashboard", label: "Dashboard" },
  { id: "containers", label: "Containers" },
  { id: "stacks", label: "Stacks" },
  { id: "images", label: "Images" },
  { id: "volumes", label: "Volumes" },
  { id: "networks", label: "Networks" },
];

const Tile: React.FC<{ icon: React.ReactNode; value: React.ReactNode; label: string; sub?: React.ReactNode; onClick: () => void }> = ({ icon, value, label, sub, onClick }) => (
  <button type="button" className="dkr-tile" onClick={onClick}>
    <span className="dkr-tile-ico">{icon}</span>
    <span className="dkr-tile-body">
      <b>{value}</b>
      <span>{label}</span>
      {sub && <i>{sub}</i>}
    </span>
  </button>
);

const Table: React.FC<{ head: string[]; empty: string; rows: React.ReactNode[][]; loading?: boolean; error?: string }> = ({ head, empty, rows, loading, error }) => (
  <div className="dkr-tablewrap">
    <table className="dkr-table dkr-table--static">
      <thead>
        <tr>
          {head.map((h) => (
            <th key={h}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr>
            <td colSpan={head.length} className="dkr-empty">
              Loading…
            </td>
          </tr>
        ) : error ? (
          <tr>
            <td colSpan={head.length} className="dkr-empty dkr-bad">
              {error}
            </td>
          </tr>
        ) : rows.length === 0 ? (
          <tr>
            <td colSpan={head.length} className="dkr-empty">
              {empty}
            </td>
          </tr>
        ) : (
          rows.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => (
                <td key={j}>{c}</td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

export const HostView: React.FC<Props> = ({ host, canGoBack, onBack, onEdit, onDelete }) => {
  const [view, setView] = useState<View>("dashboard");
  const [container, setContainer] = useState<{ id: string; tab: ContainerTab } | null>(null);
  const [stack, setStack] = useState<string | undefined>();

  const { data: info, isError: infoError, error: infoErr } = useQuery({
    queryKey: ["docker-info", host.id],
    queryFn: () => getDockerInfo(host.id),
    refetchInterval: 15_000,
    retry: false,
  });
  const { data: live = [] } = useQuery({
    queryKey: ["docker-live", host.id],
    queryFn: () => getDockerLiveContainers(host.id),
    refetchInterval: 10_000,
    retry: false,
  });
  const images = useQuery({ queryKey: ["docker-images", host.id], queryFn: () => getDockerImages(host.id), retry: false });
  const volumes = useQuery({ queryKey: ["docker-volumes", host.id], queryFn: () => getDockerVolumes(host.id), retry: false });
  const networks = useQuery({ queryKey: ["docker-networks", host.id], queryFn: () => getDockerNetworks(host.id), retry: false });

  const stacks = useMemo(() => {
    const m = new Map<string, DockerLiveContainer[]>();
    for (const c of live) if (c.project) m.set(c.project, [...(m.get(c.project) ?? []), c]);
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [live]);

  const running = live.filter((c) => c.state === "running").length;
  const failing = live.filter((c) => c.state === "restarting" || c.state === "dead").length;
  const paused = live.filter((c) => c.state === "paused").length;
  const stopped = live.length - running - failing - paused;
  const imageBytes = (images.data ?? []).reduce((n, i) => n + i.size, 0);
  const state = !host.enabled ? "idle" : infoError ? "down" : info ? (failing ? "warn" : "up") : "idle";

  const open = (id: string, tab: ContainerTab = "details") => setContainer({ id, tab });
  const go = (v: View) => {
    setContainer(null);
    if (v !== "containers") setStack(undefined);
    setView(v);
  };

  if (container) {
    return (
      <div className="gh sv dkr">
        <ContainerPage
          host={host}
          containerId={container.id}
          tab={container.tab}
          onTab={(tab) => setContainer({ id: container.id, tab })}
          onBack={() => setContainer(null)}
        />
      </div>
    );
  }

  return (
    <div className="gh sv dkr">
      <div className="rt-head">
        {canGoBack && (
          <button type="button" className="gh-btn" onClick={onBack}>
            <ArrowLeftIcon /> Environments
          </button>
        )}
        <div style={{ minWidth: 0 }}>
          <h2>{host.name}</h2>
          <p>
            {host.host}
            {info ? ` · Docker ${info.serverVersion} · ${info.os}` : ""}
          </p>
        </div>
        <span className={`rt-badge rt-badge--${state === "idle" ? "" : state}`}>
          <StatusDot state={state} />
          {!host.enabled ? "disabled" : infoError ? "unreachable" : info ? "up" : "checking"}
        </span>
        <div className="rt-head-act">
          <button type="button" className="gh-btn" onClick={onEdit}>
            <PencilIcon /> Edit
          </button>
          <button type="button" className="gh-btn gh-btn--danger" onClick={onDelete}>
            <TrashIcon /> Delete
          </button>
        </div>
      </div>

      <div className="dkr-tabs" role="tablist">
        {NAV.map((n) => (
          <button key={n.id} type="button" role="tab" aria-selected={view === n.id} className="dkr-tab" onClick={() => go(n.id)}>
            {n.label}
            {n.id === "containers" && <em>{live.length}</em>}
            {n.id === "stacks" && <em>{stacks.length}</em>}
            {n.id === "images" && images.data && <em>{images.data.length}</em>}
            {n.id === "volumes" && volumes.data && <em>{volumes.data.length}</em>}
            {n.id === "networks" && networks.data && <em>{networks.data.length}</em>}
          </button>
        ))}
      </div>

      {infoError && view === "dashboard" && <div className="rt-error">{(infoErr as Error).message}</div>}

      {view === "dashboard" && (
        <>
          <div className="dkr-tiles">
            <Tile
              icon={<CubeIcon />}
              value={live.length}
              label="Containers"
              onClick={() => go("containers")}
              sub={
                <>
                  <span className="dkr-ok">{running} running</span>
                  {stopped > 0 && <> · {stopped} stopped</>}
                  {paused > 0 && <> · {paused} paused</>}
                  {failing > 0 && (
                    <>
                      {" "}
                      · <span className="dkr-bad">{failing} failing</span>
                    </>
                  )}
                </>
              }
            />
            <Tile icon={<Square3Stack3DIcon />} value={stacks.length} label="Stacks" onClick={() => go("stacks")} sub={`${stacks.reduce((n, [, c]) => n + c.length, 0)} containers`} />
            <Tile icon={<PhotoIcon />} value={images.data?.length ?? info?.images ?? "—"} label="Images" onClick={() => go("images")} sub={images.data ? fmtSize(imageBytes) : undefined} />
            <Tile icon={<CircleStackIcon />} value={volumes.data?.length ?? "—"} label="Volumes" onClick={() => go("volumes")} sub={volumes.data ? `${volumes.data.filter((v) => v.refCount === 0).length} unused` : undefined} />
            <Tile icon={<GlobeAltIcon />} value={networks.data?.length ?? "—"} label="Networks" onClick={() => go("networks")} />
          </div>

          <div className="rt-grid">
            <div className="rt-box rt-c6">
              <h3>Environment</h3>
              <dl className="rt-kv">
                <dt>Engine</dt>
                <dd>{info ? `Docker ${info.serverVersion}` : "—"}</dd>
                <dt>OS</dt>
                <dd>{info?.os ?? "—"}</dd>
                <dt>Kernel</dt>
                <dd>{info?.kernel ?? "—"}</dd>
                <dt>Storage driver</dt>
                <dd>{info?.driver ?? "—"}</dd>
                <dt>CPUs</dt>
                <dd>{info?.cpus ?? "—"}</dd>
                <dt>Memory</dt>
                <dd>{info ? fmtSize(info.memTotal) : "—"}</dd>
                <dt>Address</dt>
                <dd className="dkr-mono">{host.host}</dd>
              </dl>
            </div>
            <div className="rt-box rt-c6">
              <h3>
                Needs attention
                <span>{live.filter((c) => c.state !== "running").length}</span>
              </h3>
              {live.filter((c) => c.state !== "running").length === 0 ? (
                <span className="dkr-ok">All containers are running.</span>
              ) : (
                <div className="dkr-attn">
                  {live
                    .filter((c) => c.state !== "running")
                    .slice(0, 8)
                    .map((c) => (
                      <button key={c.id} type="button" onClick={() => open(c.id)}>
                        <StateBadge state={c.state} />
                        <b>{c.name}</b>
                        <span className="dkr-dim">{c.status}</span>
                      </button>
                    ))}
                </div>
              )}
              {info && info.cpus > 0 && (
                <>
                  <h3>Capacity</h3>
                  <div className="dkr-cap">
                    <span>
                      {running}/{live.length} running
                    </span>
                    <Bar pct={live.length ? (running / live.length) * 100 : 0} warn={101} crit={101} />
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {view === "containers" && <ContainersView host={host} stack={stack} onClearStack={() => setStack(undefined)} onOpen={open} />}

      {view === "stacks" && (
        <Table
          head={["Stack", "Containers", "State", "Services"]}
          empty="No Compose stacks on this host."
          rows={stacks.map(([name, cs]) => {
            const up = cs.filter((c) => c.state === "running").length;
            return [
              <button key="n" type="button" className="dkr-linkcell" onClick={() => { setStack(name); setView("containers"); }}>
                {name}
              </button>,
              `${up} / ${cs.length} running`,
              <span key="s" className={`dkr-state dkr-state--${up === cs.length ? "ok" : up === 0 ? "off" : "warn"}`}>
                <i />
                {up === cs.length ? "up" : up === 0 ? "down" : "partial"}
              </span>,
              <span key="sv" className="dkr-dim">
                {cs
                  .map((c) => c.service ?? c.name)
                  .sort()
                  .join(", ")}
              </span>,
            ];
          })}
        />
      )}

      {view === "images" && (
        <Table
          loading={images.isLoading}
          error={images.isError ? (images.error as Error).message : undefined}
          head={["Tags", "ID", "Size", "Created", "Used by"]}
          empty="No images."
          rows={(images.data ?? [])
            .slice()
            .sort((a, b) => b.created - a.created)
            .map((im) => [
              im.tags.length ? im.tags.map((t) => <span key={t} className="dkr-tag">{t}</span>) : <span className="dkr-dim">&lt;none&gt;</span>,
              <span key="id" className="dkr-mono">{shortID(im.id)}</span>,
              fmtSize(im.size),
              <span key="c" className="dkr-dim">{fmtDate(im.created)}</span>,
              im.containers > 0 ? `${im.containers} container${im.containers === 1 ? "" : "s"}` : <span key="u" className="dkr-state dkr-state--warn"><i />unused</span>,
            ])}
        />
      )}

      {view === "volumes" && (
        <Table
          loading={volumes.isLoading}
          error={volumes.isError ? (volumes.error as Error).message : undefined}
          head={["Name", "Driver", "Mount point", "Size", "Used by"]}
          empty="No volumes."
          rows={(volumes.data ?? [])
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((v) => [
              <span key="n" className="dkr-mono" title={v.name}>{v.name.length > 40 ? `${v.name.slice(0, 18)}…${v.name.slice(-18)}` : v.name}</span>,
              v.driver,
              <span key="m" className="dkr-mono dkr-dim">{v.mountpoint}</span>,
              fmtSize(v.size),
              v.refCount > 0 ? `${v.refCount} container${v.refCount === 1 ? "" : "s"}` : v.refCount === 0 ? <span key="u" className="dkr-state dkr-state--warn"><i />unused</span> : "—",
            ])}
        />
      )}

      {view === "networks" && (
        <Table
          loading={networks.isLoading}
          error={networks.isError ? (networks.error as Error).message : undefined}
          head={["Name", "Driver", "Scope", "Subnet", "Gateway", "Created"]}
          empty="No networks."
          rows={(networks.data ?? [])
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((n) => [
              <b key="n">{n.name}{n.internal ? " (internal)" : ""}</b>,
              n.driver,
              n.scope,
              <span key="s" className="dkr-mono">{n.subnets.join(", ") || "—"}</span>,
              <span key="g" className="dkr-mono">{n.gateways.join(", ") || "—"}</span>,
              <span key="c" className="dkr-dim">{fmtDate(n.created)}</span>,
            ])}
        />
      )}
    </div>
  );
};
